import { Component, Inject, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { ObjectTypes } from '@common/enums/object-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { selectCurrentViewState } from '@common/store/navigation/selectors/viewstate.selectors';
import { StoryHttpActions } from '@common/store/story/actions/story.actions';
import { Store } from '@ngrx/store';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'st-create-new-template',
    templateUrl: './create-new-template.html',
    styleUrls: ['./create-new-template.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-create-new-template',
    },
})
export class CreateNewTemplateComponent extends EnpsComponent implements OnInit {
    template: StorytellingRecord;
    newTitleValue = signal<string>('');
    loading = signal(false);

    get isDisabled(): boolean {
        return !this.data.Title || !this.data.Title.trim() || this.loading();
    }

    get isSuperVisor(): boolean {
        return this.nomWebServicesJson?.HelloResults?.Supervisor;
    }
    private store = inject(Store);
    public ObjectTypes = ObjectTypes;

    destroyed$: Subject<void> = new Subject<void>();
    protected _vs: ViewStateRecord;
    vs$ = this.store
        .select(selectCurrentViewState)
        .pipe(
            tap(vs => {
                this._vs = vs ? vs : null;
            }),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    constructor(
        public navigationService: NavigationService,
        public nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public topicService: TopicService,
        public dialogRef: MatDialogRef<CreateNewTemplateComponent>,
        public playbookService: PlaybookService,
        public viewStateService: LayoutState,
        public globalTablesService: GlobalTablesService,
        @Inject(MAT_DIALOG_DATA) public data: StorytellingRecord
    ) {
        super();
    }
    ngOnInit(): void {
        this.template = this.data;
        this.newTitleValue.set(this.data.Title);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    public async createTemplate(event: any, path: string) {
        this.loading.set(true);
        this.navigationService.closeCreateNewTemplate(event);
        // TODO: move to store
        if (this.data.Type === ObjectTypes.TOPIC) {
            await this.topicService.createTopicContent(this.data.Database, path, this.data?.Guid, this.data.Path.groupId, false, null, null, null, this.newTitleValue());

            this.store.dispatch(PlaybookApiActions.loadTopicTemplates());
        } else {
            this.store.dispatch(StoryHttpActions.createStoryTemplate({ story: this.data as StorytellingStoryRecord, path }));
        }
        this.closeDialog();
    }

    public async createPersonalTemplate(event: any) {
        const destinationPath = `U_${this.nomWebServicesJson.UserId.toUpperCase()}\\L`;
        await this.createTemplate(event, destinationPath);
    }

    public async createTeamTemplate(event: any) {
        switch (this.data.Type) {
            case ObjectTypes.TOPIC:
                await this.createTemplate(event, `P_${this.navigationService.currentPlaybookTeam}\\L`);
                break;
            case ObjectTypes.SCRIPT:
                await this.createTemplate(event, `P_${this.navigationService.currentPlaybookTeam}\\X`);
                break;
        }
    }

    public async createEnterpriseTemplate(event: any) {
        switch (this.data.Type) {
            case ObjectTypes.TOPIC:
                await this.createTemplate(event, `P_${this.globalTablesService.EnterprisePlaybookGroup}\\L`);
                break;
            case ObjectTypes.SCRIPT:
                await this.createTemplate(event, `P_${this.globalTablesService.EnterprisePlaybookGroup}\\X`);
                break;
        }
    }
}
